import { useState } from "react";
import { Plus, StickyNote } from "lucide-react";
import type { Note } from "@hub/shared";
import { Button, EmptyState, Input, Select, Spinner } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { Page } from "@/app/layout/Page";
import { NoteCard } from "@/features/contact/ContactSections";
import { NoteEditor } from "@/features/contact/NoteEditor";
import { useSelection } from "@/app/navigation";
import s from "../shared.module.css";

export function NotesPage() {
  const [query, setQuery] = useState("");
  const [label, setLabel] = useState("");
  const [editing, setEditing] = useState<Note | "new" | null>(null);
  const [limit, setLimit] = useState(50);
  const focused = useSelection((st) => st.sidebarContactId ?? st.focusedContactId);
  const notes = useEngineQuery("getAllNotes", { query: query || undefined, label: label || null, limit, offset: 0 }, { placeholderData: (p) => p });
  const labels = useEngineQuery("getNoteLabels", undefined);
  const del = useEngineMutation("deleteNote", { invalidate: ["getAllNotes", "getContact", "getNoteLabels"] });
  return (
    <Page title="Notes" actions={<Button size="sm" variant="primary" icon={<Plus size={13} />} onClick={() => setEditing("new")}>New note</Button>}>
      <div className="u-row" style={{ marginBottom: 14 }}>
        <Input data-search-box placeholder="Search notes and people" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Select value={label} onChange={(e) => setLabel(e.target.value)} style={{ width: 170 }} aria-label="Label">
          <option value="">All labels</option>
          {(labels.data ?? []).map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
      </div>
      {notes.isLoading && <Spinner />}
      {notes.isSuccess && notes.data.items.length === 0 && (
        <EmptyState icon={<StickyNote size={26} className="u-muted" />} title={query || label ? "No notes match" : "No notes yet"}>
          Notes you write about people and groups show up here, searchable and filterable by label.
        </EmptyState>
      )}
      {notes.data?.items.map((n) => <NoteCard key={n.id} note={n} contactName={n.contactName ?? n.groupName} showContact onEdit={() => setEditing(n)} onDelete={() => { if (window.confirm("Delete this note?")) del.mutate({ noteId: n.id }); }} />)}
      {notes.data && notes.data.nextOffset !== null && <Button variant="ghost" full onClick={() => setLimit((l) => l + 50)}>Show more</Button>}
      <NoteEditor open={editing !== null} onClose={() => setEditing(null)} note={editing === "new" ? null : editing} contactId={editing === "new" ? focused : undefined} />
    </Page>
  );
}
